import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { CommunityPostCard } from './CommunityShared';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from './communityUtils';
import './Community.css';

export default function PublicCommunityPost() {
  const { t } = useTranslation();
  const { postId } = useParams();
  const viewerState = useQuery(api.community.getViewerState, {});
  const post = useQuery(api.community.getPost, postId ? { postId } : 'skip');
  const comments = useQuery(api.community.listComments, postId ? { postId } : 'skip');
  const addComment = useMutation(api.community.addComment);
  const deleteComment = useMutation(api.community.deleteComment);
  const followAuthor = useMutation(api.community.followAuthor);
  const unfollowAuthor = useMutation(api.community.unfollowAuthor);
  const savePost = useMutation(api.community.savePost);
  const unsavePost = useMutation(api.community.unsavePost);
  const createPollVote = useMutation(api.community.createPollVote);
  const reportEntity = useMutation(api.community.reportEntity);
  const [commentBody, setCommentBody] = useState('');
  const [isCommenting, setIsCommenting] = useState(false);
  const [message, setMessage] = useState('');

  if (post === undefined || comments === undefined) {
    return (
      <div className="community-public-shell">
        <div className="community-panel" style={{ padding: 40, textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="community-public-shell">
        <section className="community-panel" style={{ padding: 40, textAlign: 'center' }}>
          <h3>{t("community.postUnavailable")}</h3>
          <p style={{ marginTop: 8 }}>{t("community.postNotFound")}</p>
          <Link className="btn btn-primary btn-sm" to="/projects/community" style={{ marginTop: 16 }}>{t("community.openCommunity")}</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="community-public-shell">
      <div className="community-header-actions">
        <Link className="community-back-link" to={`/community/${post.author.handle}`}>
          {t('common.back')} @{post.author.handle}
        </Link>
        <Link className="btn btn-secondary btn-sm" to="/projects/community">{t('common.community')}</Link>
        <Link className="btn btn-ghost btn-sm" to="/projects">{t('common.projects')}</Link>
      </div>

      {message ? (
        <div className={`community-banner ${message.includes('Report') || message.includes('Comment') ? 'success' : 'error'}`} style={{ marginBottom: 14 }}>
          {message}
        </div>
      ) : null}

      <CommunityPostCard
        post={post}
        viewerProfile={viewerState?.profile}
        onToggleFollow={async (authorId) => {
          try {
            if (post.viewer?.isFollowingAuthor) {
              await unfollowAuthor({ authorId });
            } else {
              await followAuthor({ authorId });
            }
          } catch (error) {
            setMessage(error.message || t('community.couldNotUpdateFollowStatus'));
          }
        }}
        onToggleSave={async (item) => {
          try {
            if (item.viewer?.isSaved) {
              await unsavePost({ postId: item._id });
            } else {
              await savePost({ postId: item._id });
            }
          } catch (error) {
            setMessage(error.message || t('community.couldNotUpdateSavedState'));
          }
        }}
        onVotePoll={async (nextPostId, optionId) => {
          try {
            await createPollVote({ postId: nextPostId, optionId });
          } catch (error) {
            setMessage(error.message || t('community.couldNotRecordVote'));
          }
        }}
        onReportPost={async (item) => {
          const reason = window.prompt(t('community.reportPost'));
          if (!reason) return;
          try {
            await reportEntity({ targetType: 'post', targetId: item._id, reason });
            setMessage(t('community.reportSubmitted'));
          } catch (error) {
            setMessage(error.message || t('community.couldNotSubmitReport'));
          }
        }}
      />

      <section className="community-panel" style={{ marginTop: 14 }}>
        <div className="community-panel-head">
          <div>
            <p className="community-eyebrow">{t("community.discussion")}</p>
            <h3>{comments.length} {comments.length === 1 ? t('community.commentCount', {count: 1}) : t('community.commentCount_plural', {count: comments.length})}</h3>
          </div>
        </div>

        {viewerState?.profile ? (
          <div className="community-comment-form" style={{ marginTop: 14 }}>
            <textarea
              className="input textarea"
              rows={3}
              value={commentBody}
              onChange={(event) => setCommentBody(event.target.value)}
              placeholder={t("community.commentPlaceholder")}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={async () => {
                setIsCommenting(true);
                try {
                  await addComment({ postId: post._id, body: commentBody });
                  setCommentBody('');
                  setMessage(t('community.commentAdded'));
                  setTimeout(() => setMessage(''), 3000);
                } catch (error) {
                  setMessage(error.message || t('community.couldNotAddComment'));
                } finally {
                  setIsCommenting(false);
                }
              }}
              disabled={!commentBody.trim() || isCommenting}
            >
              {isCommenting ? t('common.posting') : t('community.addCommentBtn')}
            </button>
          </div>
        ) : (
          <p className="community-empty-copy">{t('community.signInToJoin')}</p>
        )}

        <div className="community-stack" style={{ marginTop: 14 }}>
          {comments.map((comment) => (
            <article key={comment._id} className="community-comment-card">
              <div className="community-comment-head">
                <div>
                  <Link to={`/community/${comment.author.handle}`}>{comment.author.displayName}</Link>
                  <span>@{comment.author.handle}</span>
                </div>
                <div className="community-inline-actions">
                  <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                    {formatRelativeTime(t, comment.createdAt)}
                  </span>
                  {comment.canDelete ? (
                    <button
                      className="community-link-button"
                      onClick={async () => {
                        await deleteComment({ commentId: comment._id });
                        setMessage(t('community.commentRemoved'));
                        setTimeout(() => setMessage(''), 3000);
                      }}
                    >{t('community.deleteComment')}</button>
                  ) : null}
                </div>
              </div>
              <p>{comment.body}</p>
            </article>
          ))}
          {!comments.length ? <p className="community-empty-copy">{t('community.noCommentsYet')}</p> : null}
        </div>
      </section>
    </div>
  );
}
